import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useLang } from "../contexts/LanguageContext";
import CategoryPicker from "../components/CategoryPicker";
import { categoryLabel } from "../lib/categories";

export default function UploadPage() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [suggesting, setSuggesting] = useState(false);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const handleSuggest = async () => {
    if (!file) return;
    setSuggesting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/categories/suggest", fd);
      if (data?.slug) {
        setCategory(data.slug);
        toast.success(
          t("suggested_category").replace("{label}", categoryLabel(data.slug, lang))
        );
      } else {
        toast.info(t("no_suggestion"));
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSuggesting(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return;
    setBusy(true);
    setErr("");
    const form = new FormData();
    form.append("file", file);
    form.append("title", title || "Untitled");
    form.append("description", description);
    if (category) form.append("category", category);
    try {
      const { data } = await api.post("/photos", form);
      navigate(`/photo/${data.id}`);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-[1200px] px-6 sm:px-10 py-16" data-testid="upload-page">
      <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-zinc-500">
        {t("brand")}
      </div>
      <h1 className="mt-3 font-serif font-light text-4xl sm:text-5xl text-zinc-50 tracking-tight">
        {t("upload_title")}
      </h1>
      <p className="mt-4 text-zinc-400 font-light max-w-xl">{t("upload_desc")}</p>

      <form onSubmit={submit} className="mt-10 grid gap-10 lg:grid-cols-12" data-testid="upload-form">
        <div className="lg:col-span-7">
          <label
            htmlFor="file-input"
            className="block border border-dashed border-zinc-800 hover:border-zinc-600 transition-colors aspect-[4/3] bg-zinc-950 cursor-pointer overflow-hidden"
            data-testid="upload-dropzone"
          >
            {preview ? (
              <img src={preview} alt="preview" className="w-full h-full object-contain bg-zinc-900" />
            ) : (
              <div className="h-full w-full flex flex-col items-center justify-center text-zinc-500 font-mono text-[11px] uppercase tracking-[0.3em]">
                <div>{t("file")}</div>
                <div className="mt-2 text-zinc-600 text-[10px]">JPG · PNG · max 25MB</div>
              </div>
            )}
          </label>
          <input
            id="file-input"
            type="file"
            accept="image/*"
            required
            onChange={onFile}
            className="hidden"
            data-testid="upload-file-input"
          />
        </div>

        <div className="lg:col-span-5 space-y-5">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
              {t("photo_title")}
            </label>
            <input
              required
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="mt-2 w-full bg-transparent border border-zinc-800 px-4 py-3 text-zinc-100 font-light focus:outline-none focus:border-zinc-500"
              data-testid="upload-title-input"
            />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
              {t("photo_description")}
            </label>
            <textarea
              rows={5}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="mt-2 w-full bg-transparent border border-zinc-800 px-4 py-3 text-zinc-100 font-light focus:outline-none focus:border-zinc-500 resize-none"
              data-testid="upload-description-input"
            />
          </div>
          <div>
            <div className="flex items-center justify-between gap-3">
              <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                {t("category_optional")}
              </label>
              <button
                type="button"
                onClick={handleSuggest}
                disabled={!file || suggesting}
                className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-sienna hover:text-sienna-400 disabled:text-zinc-700 disabled:cursor-not-allowed transition-colors"
                data-testid="upload-suggest-category-button"
              >
                <Sparkles size={11} className={suggesting ? "animate-pulse" : ""} />
                {suggesting ? t("suggesting") : t("suggest_category")}
              </button>
            </div>
            <div className="mt-3">
              <CategoryPicker value={category} onChange={setCategory} idPrefix="upload-cat" />
            </div>
          </div>
          {err && (
            <div className="border border-red-900 bg-red-950/30 text-red-300 px-4 py-3 text-sm" data-testid="upload-error">
              {err}
            </div>
          )}
          <button
            type="submit"
            disabled={busy || !file}
            className="w-full bg-sienna text-white py-3 text-sm font-light hover:bg-sienna-400 disabled:opacity-50 transition-colors"
            data-testid="upload-submit-button"
          >
            {busy ? t("uploading") : t("submit")}
          </button>
        </div>
      </form>
    </div>
  );
}
